import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { SEO } from '../components/common/SEO';
import { Tooltip } from '../components/ui/Tooltip';
import { WishingWellService } from '../services/wishingWellService';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { 
  Sparkles, 
  Star, 
  Heart, 
  Zap, 
  Wand2,
  Lock,
  Utensils,
  Coffee,
  ShoppingBag,
  Users
} from 'lucide-react';

interface Wish {
  id: string;
  title: string;
  description: string;
  category: string;
  votes: number;
  status: 'pending' | 'in-progress' | 'completed';
  created_at: string;
  user_id?: string;
  user_has_voted?: boolean;
}


const WISH_CATEGORIES = [
  { id: 'ui', label: 'User Interface', icon: Sparkles, color: 'text-brown-500' },
  { id: 'features', label: 'New Features', icon: Star, color: 'text-brown-500' },
  { id: 'performance', label: 'Performance', icon: Zap, color: 'text-brown-500' },
  { id: 'integration', label: 'Integrations', icon: Utensils, color: 'text-brown-500' },
  { id: 'mobile', label: 'Mobile App', icon: Users, color: 'text-brown-500' },
];

export const WishingWellPage: React.FC = () => {
  const { user, signInWithGoogle, signInWithPasskey } = useAuth();
  const [wishTitle, setWishTitle] = useState('');
  const [wishDescription, setWishDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('features');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentWishes, setRecentWishes] = useState<Wish[]>([]);
  const [loadingWishes, setLoadingWishes] = useState(true);

  useEffect(() => {
    loadRecentWishes();
  }, []);

  const handlePasskeySignIn = async () => {
    try {
      await signInWithPasskey();
      toast.success('Welcome back! Signed in with passkey.');
    } catch (error: any) {
      toast.error(error.message || 'Passkey authentication failed');
    }
  };

  const loadRecentWishes = async () => {
    try {
      setLoadingWishes(true);
      const wishes = await WishingWellService.getRecentWishes();
      setRecentWishes(wishes);
    } catch (error) {
      console.error('Failed to load recent wishes:', error);
    } finally {
      setLoadingWishes(false);
    }
  };


  const handleSubmitWish = async () => {
    if (!user) {
      toast.error('Please sign in to submit wishes.', {
        duration: 4000,
        icon: '🔒',
      });
      return;
    }

    if (!wishTitle.trim() || !wishDescription.trim()) {
      toast.error('Please fill in both title and description');
      return;
    }

    try {
      setIsSubmitting(true);
      
      await WishingWellService.submitWish({
        title: wishTitle.trim(),
        description: wishDescription.trim(),
        category: selectedCategory
      });

      toast.success('✨ Your wish has been cast into the well!');
      
      // Reset form
      setWishTitle('');
      setWishDescription('');
      
      // Reload recent wishes
      loadRecentWishes();
      
    } catch (error) {
      console.error('Error submitting wish:', error);
      toast.error('Failed to submit wish. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoteWish = async (wishId: string) => {
    if (!user) {
      toast.error('Please sign in to vote on wishes.', {
        duration: 4000,
        icon: '🔒',
      });
      return;
    }
    try {
      await WishingWellService.voteWish(wishId);
      toast.success('Vote cast! 🌟');
      loadRecentWishes();
    } catch (error) {
      toast.error('Failed to vote. Please try again.');
    }
  };

  const getCategoryIcon = (categoryId: string) => {
    const category = WISH_CATEGORIES.find(c => c.id === categoryId);
    return category?.icon || Star;
  };

  const getCategoryColor = (categoryId: string) => {
    const category = WISH_CATEGORIES.find(c => c.id === categoryId);
    return category?.color || 'text-gray-600';
  };

  return (
    <>
      <SEO 
        title="Wishing Well - Feature Requests | Wishful"
        description="Cast your wishes for new features and improvements into our magical wishing well. Help shape the future of Wishful!"
        keywords={["feature requests", "wishful", "improvements", "suggestions", "feedback"]}
      />
      
          <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white/90 backdrop-blur-md border-b border-secondary-200 sticky top-0 z-10 shadow-soft">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-medium">
                  <Wand2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Wishing Well</h1>
                  <p className="text-base text-gray-600 font-medium">Cast your wishes for Wishful's future</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Customer-Centric Hero Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-medium">
                <Utensils className="w-8 h-8 text-white" />
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center shadow-medium">
                <Coffee className="w-8 h-8 text-white" />
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-medium">
                <ShoppingBag className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What would make your wishlist experience better?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Help us build the features you actually want. Your feedback shapes the future of Wishful.
            </p>
          </div>

          {/* Social Proof Section */}
          <div className="mb-12">
            <Card className="bg-white border border-gray-200">
              <div className="text-center">
                <div className="flex justify-center space-x-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Heart key={i} className="w-6 h-6 text-primary-500 fill-current" />
                  ))}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Loved by thousands of users
                </h3>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  "Wishful has completely transformed how I organize my gift ideas. The interface is so intuitive and the features keep getting better!"
                </p>
                <p className="text-sm text-gray-500 mt-3 font-medium">- Sarah M., Happy User</p>
              </div>
            </Card>
          </div>

          {/* Login Prompt for Guest Users */}
          {!user && (
            <Card className="mb-8 bg-white border border-gray-200">
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-medium">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Sign In Required</h3>
                  <p className="text-gray-600 mb-4 text-base leading-relaxed">
                    You can view wishes below, but you need to sign in to submit new wishes or vote on existing ones.
                  </p>
                  <div className="flex space-x-4">
                    <Button
                      onClick={signInWithGoogle}
                      size="lg"
                      className="shadow-medium"
                    >
                      Sign In with Google
                    </Button>
                    <Button
                      onClick={handlePasskeySignIn}
                      size="lg"
                      variant="outline"
                      className="shadow-medium"
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <circle cx="12" cy="16" r="1"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Sign In with Passkey
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Wishing Well */}
            <div className="space-y-6">
              <Card className="bg-white border border-gray-200">
                <div className="text-center mb-8">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-large">
                    <Wand2 className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Cast Your Wish</h2>
                  <p className="text-gray-600 text-lg font-medium">Share your ideas to make Wishful even better</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Input
                      label="Wish Title"
                      value={wishTitle}
                      onChange={(e) => setWishTitle(e.target.value)}
                      placeholder="e.g., Dark Mode for Dashboard"
                      className="w-full"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={wishDescription}
                      onChange={(e) => setWishDescription(e.target.value)}
                      placeholder="Describe your wish in more detail..."
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 transition-all duration-200 bg-white shadow-soft resize-y"
                      rows={4}
                      maxLength={500}
                    />
                    <div className="text-right text-sm text-gray-500 mt-2 font-medium">
                      {wishDescription.length}/500
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Category
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {WISH_CATEGORIES.map((category) => {
                        const Icon = category.icon;
                        return (
                          <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            disabled={isSubmitting}
                            className={`px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                              selectedCategory === category.id
                                ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-medium'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center space-x-2">
                              <Icon className={`w-4 h-4 ${selectedCategory === category.id ? 'text-white' : category.color}`} />
                              <span>{category.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    onClick={handleSubmitWish}
                    disabled={isSubmitting || !wishTitle.trim() || !wishDescription.trim()}
                    size="lg"
                    className={`w-full shadow-medium ${
                      user 
                        ? '' 
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-3">
                        <LoadingSpinner size="sm" />
                        <span>Casting Wish...</span>
                      </div>
                    ) : user ? (
                      <div className="flex items-center space-x-3">
                        <Wand2 className="w-5 h-5" />
                        <span>Cast Wish Into Well</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <Lock className="w-5 h-5" />
                        <span>Sign In to Cast Wish</span>
                      </div>
                    )}
                  </Button>
                </div>
              </Card>

            </div>

            {/* Recent Wishes */}
            <div className="space-y-6">
              <Card className="bg-white border border-gray-200">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center shadow-medium">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Recent Wishes</h3>
                </div>

                {loadingWishes ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : recentWishes.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Wand2 className="w-8 h-8 text-secondary-500" />
                    </div>
                    <p className="text-secondary-600 text-lg font-medium">No wishes cast yet. Be the first!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentWishes.map((wish) => {
                      const Icon = getCategoryIcon(wish.category);
                      const colorClass = getCategoryColor(wish.category);
                      
                      return (
                        <div
                          key={wish.id}
                          className="p-5 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-medium transition-all duration-300"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Icon className={`w-4 h-4 ${colorClass}`} />
                              <span className="text-sm font-medium text-gray-600 capitalize">
                                {WISH_CATEGORIES.find(c => c.id === wish.category)?.label}
                              </span>
                            </div>
                            <Tooltip 
                              content={!user ? 'Please log in to vote on wishes' : ''}
                              disabled={!!user}
                            >
                              <button
                                onClick={() => handleVoteWish(wish.id)}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                                  wish.user_has_voted 
                                    ? 'bg-gradient-to-r from-accent-500 to-accent-600 text-white shadow-medium' 
                                    : !user 
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                <Heart className={`w-4 h-4 ${wish.user_has_voted ? 'fill-current' : ''}`} />
                                <span>{wish.votes}</span>
                              </button>
                            </Tooltip>
                          </div>
                          
                          <h4 className="font-bold text-gray-900 mb-3 text-lg">{wish.title}</h4>
                          <p className="text-gray-600 mb-4 leading-relaxed">{wish.description}</p>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500 font-medium">{new Date(wish.created_at).toLocaleDateString()}</span>
                            <span className={`px-3 py-1 rounded-md text-sm font-semibold ${
                              wish.status === 'completed' ? 'bg-green-100 text-green-700' :
                              wish.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {wish.status.replace('-', ' ')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* How It Works */}
              <Card className="bg-white border border-gray-200">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center shadow-medium">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">How It Works</h3>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      1
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Cast Your Wish</h4>
                      <p className="text-gray-600 leading-relaxed">Describe what you'd like to see in Wishful</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      2
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Vote for Favorites</h4>
                      <p className="text-gray-600 leading-relaxed">Others can vote on your wish with hearts</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      3
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Track Progress</h4>
                      <p className="text-gray-600 leading-relaxed">Popular wishes get prioritized for development</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
